import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { useMemo } from "react";

import BackgroundImage from "../../shared/image/placeholder.jpg";
import { buildAggregateGroupRowKey } from "../../services/beefAiPricing";
import InsightsImageCard from "./design/ImageCard";
import {
  buildAggregateCardMetaChips,
  buildAggregateCardPrimaryMetric,
  formatImageCardRetailerProvinceDescription,
  formatInsightsImageCardAriaLabel,
} from "./pricingReportCardDisplay";
import {
  PRICING_REPORT_AGGREGATE_CARD_IMAGE_TRANSFORM_KEY,
  withDirectusAssetTransformKey,
} from "./pricingReportImageUrls";

function PricingReportAggregateCard({ row, coverUrl, onCardClick }) {
  const retailer = useSelector((state) => state.retailer);
  const observationCount = Number(row?.n) || 0;
  const isCardClickable = observationCount > 0 && typeof onCardClick === "function";
  const aggregateRowKey = buildAggregateGroupRowKey(row?.drilldown);
  const cardCoverImageUrl = withDirectusAssetTransformKey(
    coverUrl,
    PRICING_REPORT_AGGREGATE_CARD_IMAGE_TRANSFORM_KEY
  );
  const coverImageKey = `${aggregateRowKey}:${cardCoverImageUrl || "pending"}`;

  const placeholderImageUrl = retailer?.default_image
    ? `${process.env.REACT_APP_API_URL}/assets/${retailer.default_image}`
    : BackgroundImage;

  const primaryMetric = useMemo(() => buildAggregateCardPrimaryMetric(row), [row]);
  const metaChips = useMemo(() => buildAggregateCardMetaChips(row), [row]);
  const productTitle = row.productName || "—";
  const cardDescription = useMemo(
    () => formatImageCardRetailerProvinceDescription(row?.retailer, row?.province),
    [row?.retailer, row?.province]
  );

  const imageCard = (
    <InsightsImageCard
      key={coverImageKey}
      title={productTitle}
      description={cardDescription}
      imageUrl={cardCoverImageUrl}
      placeholderImageUrl={placeholderImageUrl}
      imageFit="cover"
      primaryMetric={primaryMetric}
      primaryMetricPlacement="text"
      metaChips={metaChips}
    />
  );

  if (!isCardClickable) {
    return <Box sx={{ height: "100%" }}>{imageCard}</Box>;
  }

  const cardAriaLabel = formatInsightsImageCardAriaLabel(
    productTitle,
    cardDescription,
    primaryMetric,
    metaChips
  );

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={cardAriaLabel || "Open pricing history gallery"}
      onClick={() => onCardClick(row)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onCardClick(row);
        }
      }}
      sx={{
        cursor: "pointer",
        height: "100%",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      {imageCard}
    </Box>
  );
}

export default PricingReportAggregateCard;
